// NOTE: the logic here is a bit confusing if you don't understand the relationship
// between snapshots and display logic. Some notes are included at:
// src/components/week-in-review-scrubber/README.md

//////////////
// IMPORTS
//////////////
// schema
import * as ISchema from "@schema";

import { deserializeBitsToTimeSlots, SlotAndIndex } from "@util/bitmask.util";
import { timeSlots } from "@constants/time-slots.constant";

//////////////
// TYPES
//////////////
export enum SlotCategory {
  AfterHours,
  Focus,
  Fragmented,
  Hold,
  Lunch,
  Meeting,
  Unassigned,
}

export interface IRenderSlot extends SlotAndIndex {
  category: SlotCategory;
}

export interface IOverallWorkingHoursInfo {
  start: { label: string; needsOffset: boolean };
  end: { label: string; needsOffset: boolean };
}

//////////////////
// SMALL HELPERS
/////////////////
// just adds a slotCategory to a slotAndIndex
function addSlotCategory(slots: SlotAndIndex[], category: SlotCategory): IRenderSlot[] {
  return slots.map((slot) => ({
    category,
    ...slot,
  }));
}

// deserializes and categories a day of bits
function deserializeDayOfBits(day: ISchema.IDaySnapshot) {
  const focusSlots = addSlotCategory(deserializeBitsToTimeSlots(day.focusTime), SlotCategory.Focus);
  const lunchSlots = addSlotCategory(deserializeBitsToTimeSlots(day.lunch), SlotCategory.Lunch);
  const meetingSlots = addSlotCategory(
    deserializeBitsToTimeSlots(day.meetings),
    SlotCategory.Meeting,
  );
  const fragmentedSlots = addSlotCategory(
    deserializeBitsToTimeSlots(day.fragmented),
    SlotCategory.Fragmented,
  );

  return [...focusSlots, ...lunchSlots, ...meetingSlots, ...fragmentedSlots];
}

// generates info needed to render a workingHour start or end label
function generateWorkingHourLabel(enumIndex: number, isStart: boolean, includeAmPmLabels = true) {
  const defaultLabel = isStart
    ? timeSlots.min[timeSlots.all[enumIndex]]
    : timeSlots.max[timeSlots.all[enumIndex]];

  const hour = Number(defaultLabel.split(":")[0]);
  const isOn30 = defaultLabel.split(":")[1][0] === "3";
  const correctedHour = hour + (isOn30 && isStart ? 1 : 0);
  const amPm = defaultLabel.indexOf("am") > -1 ? "am" : "pm";

  return {
    label: correctedHour + (includeAmPmLabels ? amPm : ""),
    needsOffset: isOn30,
  };
}

// gets info that's needed to render the start/end labels
function getOverallWorkingHoursLabels(
  startIndex: number,
  endIndex: number,
  includeAmPmLabels = true,
): IOverallWorkingHoursInfo {
  return {
    start: generateWorkingHourLabel(startIndex, true, includeAmPmLabels),
    end: generateWorkingHourLabel(endIndex, false, includeAmPmLabels),
  };
}

//////////////////////////
// SUPPORTING FUNCTIONS
//////////////////////////

// for a given snapshot, finds the earliest start working hours and latest ending working hours
function getWorkingHoursMaxMinForSnapshot(snapshot: ISchema.ICompactCalendarSlotSnapshot) {
  // deserialize the working hours bits first
  return snapshot.days
    .map((day) => {
      return deserializeBitsToTimeSlots(day.workingHours);
    })
    .reduce(
      (accum: { startIndex: number; endIndex: number }, slotAndIndex) => {
        // reduce across all snapshots to find min/max
        const firstSlot = slotAndIndex[0];
        const lastSlot = slotAndIndex[slotAndIndex.length - 1];

        const firstIndex = !!firstSlot ? firstSlot.slotIndex : timeSlots.all.length - 1;
        const lastIndex = !!lastSlot ? lastSlot.slotIndex : 0;

        // then update the accumulator with the earliest/latest
        accum.startIndex = accum.startIndex < firstIndex ? accum.startIndex : firstIndex;
        accum.endIndex = accum.endIndex > lastIndex ? accum.endIndex : lastIndex;
        return accum;
      },
      { startIndex: timeSlots.all.length - 1, endIndex: 0 },
    );
}

// for a complete set of snapshots, find the min/max working hours
// used to find the window to render the snapshot scrubber
export function getWorkingHoursMaxMin(snapshots: ISchema.ICompactCalendarSlotSnapshot[]) {
  return snapshots
    .map((snapshot) => {
      return getWorkingHoursMaxMinForSnapshot(snapshot);
    })
    .reduce((accum, next) => {
      accum.startIndex = accum.startIndex < next.startIndex ? accum.startIndex : next.startIndex;
      accum.endIndex = accum.endIndex < next.endIndex ? accum.endIndex : next.endIndex;
      return accum;
    });
}

function generateRewindRenderDay(day: ISchema.IDaySnapshot) {
  // working hours have to be processed seperately, b/c they're on top of the other categories
  const workingHours = deserializeBitsToTimeSlots(day.workingHours);
  // weekends have no working hours, so, if no length, set to out-of-bounds number
  const workingHoursStartIndex = !!workingHours.length ? workingHours[0].slotIndex : 100;
  const workingHoursEndIndex = !!workingHours.length
    ? workingHours[workingHours.length - 1].slotIndex
    : 0;

  // build out a day of 48 slots (30 min blocks) — outside workingHours is unassigned, inside is fragmented
  const processedDay: SlotCategory[] = Array(48)
    .fill(0)
    .map((_item, i) => {
      let category = SlotCategory.AfterHours;
      if (i >= workingHoursStartIndex && i <= workingHoursEndIndex) {
        // this is slightly confusing, but basically we categorize everything EXCEPT holds
        // so, if something's inside working hours, and it's NOT another category, it's a hold
        category = SlotCategory.Hold;
      }
      return category;
    });

  // now, over-write the 30 minute block with the actual info from the rewindHistory
  deserializeDayOfBits(day).forEach((slot) => {
    processedDay[slot.slotIndex] = slot.category;
  });

  return processedDay;
}

//////////////////////////
// THE BIG KAHUNA
//////////////////////////
// NOTE: this is pretty inefficient; so many nested loops...
// fortunately, it's still fast enough because the arrays are known length
// and it makes rendering easy by processing snapshots into a set of 30 minute blocks
export function generateRewindRenderData(
  snapshots: ISchema.ICompactCalendarSlotSnapshot[],
  includeAmPmLabels = true,
) {
  // NOTE: assumes snapshots return a set of 7 days
  // and to show a full week, we use M-SU week snapshots
  // see: src/components/week-in-review-scrubber/README.md
  const weekSnapshots = snapshots.slice(1, 7);
  const absoluteMinMax = getWorkingHoursMaxMin(weekSnapshots);

  // generate the render info for each snapshot
  const renderInfo = weekSnapshots.map((snapshot) => {
    // inside each week snapshot (which has a snapshot for each day, Su-Sa),
    // we're only going to display M-F
    const daySnapshots = snapshot.days.slice(1, 6);
    return daySnapshots.map((day) => {
      // note that this slices the slots down to the minimum and maximum working hours
      return generateRewindRenderDay(day).slice(absoluteMinMax.startIndex, absoluteMinMax.endIndex);
    });
  });

  const labelInfo = getOverallWorkingHoursLabels(
    absoluteMinMax.startIndex,
    absoluteMinMax.endIndex,
    includeAmPmLabels,
  );

  return {
    snapshots: renderInfo,
    overallWorkingHours: labelInfo,
  };
}
